import React, { useState } from 'react';
import { toast } from 'react-toastify';

interface UserPropTypes {
  userData: any;
  iOwnTheAccount?: boolean;
  iNeedAccessToView?: string;
  candidateId?: number;
  viewerType?: 'recruiter' | 'candidate_self' | 'anonymous';
  hasFullAccess?: boolean;
}

const WITHHELD_COPY = 'Not shared by candidate';

const IcLocation = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);
const IcPhone = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.65 3.32 2 2 0 0 1 3.62 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.78a16 16 0 0 0 5.94 5.94l1.84-1.84a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);
const IcMail = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);
const IcUser = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);
const IcClock = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);
const IcBriefcase = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
  </svg>
);
const IcLock = () => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
const IcInfo = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const FIELD_ICON: Record<string, React.ReactNode> = {
  dob: <IcClock />,
  location: <IcLocation />,
  phoneNumber: <IcPhone />,
  email: <IcMail />,
  gender: <IcUser />,
  company: <IcBriefcase />,
  relocate: <IcBriefcase />,
  workArrangements: <IcBriefcase />,
  salary: <IcBriefcase />,
  startTime: <IcClock />,
  flexibility: <IcClock />,
};

function isFieldPrivacyRestricted(
  userData: { privacySettings?: Record<string, unknown> } | undefined,
  field: string
): boolean {
  const privacy = userData?.privacySettings;
  const hiddenByPrivacy: Record<string, boolean> = {
    email: privacy?.showEmail === false,
    phoneNumber: privacy?.showPhone === false,
    location: privacy?.showLocation === false,
    salary: privacy?.showSalaryExpectations === false,
    company: privacy?.showCurrentCompany === false,
    dob: privacy?.showWorkAnniversary === false,
    workArrangements: privacy?.showWorkAnniversary === false,
    startTime: privacy?.showWorkAnniversary === false,
  };
  return Boolean(hiddenByPrivacy[field]);
}

const PublicUserInformationList: React.FC<UserPropTypes> = ({
  userData,
  iOwnTheAccount,
  iNeedAccessToView,
  hasFullAccess = false,
}) => {
  const [requestedFields, setRequestedFields] = useState<string[]>([]);
  const isReadOnlyOwner = Boolean(iOwnTheAccount);

  const needsHidden = (field: string, isPrivate: boolean) => {
    if (isReadOnlyOwner || hasFullAccess || iNeedAccessToView === 'NO') return false;
    if (isFieldPrivacyRestricted(userData, field)) return true;
    return Boolean(isPrivate && iNeedAccessToView === 'YES');
  };

  const handleRequestAccess = (field: string) => {
    if (isReadOnlyOwner) return;
    setRequestedFields((prev) => (prev.includes(field) ? prev : [...prev, field]));
    toast.info(`Request to view ${field} sent`);
  };

  const infoList = [
    {
      label: 'Date of Birth',
      field: 'dob',
      isPrivate: true,
      value: userData?.dateOfBirth || 'Not specified',
    },
    {
      label: 'Location',
      field: 'location',
      isPrivate: false,
      value:
        userData?.city || userData?.state || userData?.country
          ? `${userData?.city || ''}${userData?.state ? `, ${userData.state}` : ''}${
              userData?.country ? `, ${userData.country}` : ''
            }`
          : 'Not specified',
    },
    {
      label: 'Phone Number',
      field: 'phoneNumber',
      isPrivate: true,
      value: (() => {
        const phone = userData?.phone || userData?.phoneNumber;
        if (!phone) return 'Not specified';
        const s = String(phone);
        return s.startsWith('+') ? s : `+${s}`;
      })(),
    },
    {
      label: 'Email',
      field: 'email',
      isPrivate: true,
      value: userData?.email || 'Not specified',
    },
    {
      label: 'Gender',
      field: 'gender',
      isPrivate: false,
      value: userData?.gender || 'Not specified',
    },
    {
      label: 'Current Company',
      field: 'company',
      isPrivate: false,
      value: userData?.company || 'Not specified',
    },
    {
      label: 'Willing to Relocate',
      field: 'relocate',
      isPrivate: false,
      value: userData?.willingToRelocate || 'Not specified',
    },
    {
      label: 'Work Arrangements',
      field: 'workArrangements',
      isPrivate: false,
      value: userData?.workArrangements || 'Not specified',
    },
    ...(userData?.salary && userData.salary !== 'Not indicated'
      ? [
          {
            label: 'Salary',
            field: 'salary',
            isPrivate: true,
            value: `$ ${parseInt(String(userData.salary), 10).toLocaleString('en-US')}`,
          },
        ]
      : []),
    {
      label: 'Time to Start',
      field: 'startTime',
      isPrivate: false,
      value: userData?.timeToStart || 'Not specified',
    },
    ...(userData?.flexibleType
      ? [
          {
            label: 'Flexibility',
            field: 'flexibility',
            isPrivate: false,
            value: userData.flexibleType,
          },
        ]
      : []),
  ] as Array<{ label: string; field: string; value: React.ReactNode; isPrivate: boolean }>;

  return (
    <div className="candidate-info-card">
      <div className="candidate-info-header">
        <span>Candidate Information</span>
        <span className="candidate-info-header-icon">
          <IcInfo />
        </span>
      </div>

      {isReadOnlyOwner ? (
        <div className="candidate-info-full-access-row">
          <p className="candidate-info-readonly-note">
            This is a read-only preview of how recruiters see your public profile.
          </p>
        </div>
      ) : null}

      <div className="candidate-info-list">
        {infoList.map((row) => {
          const isHidden = needsHidden(row.field, row.isPrivate);
          const empty =
            row.value == null ||
            row.value === '' ||
            row.value === 'Not specified' ||
            row.value === 'Not indicated';
          const withheld =
            !isReadOnlyOwner &&
            hasFullAccess &&
            empty &&
            ['email', 'phoneNumber', 'dob', 'salary', 'company'].includes(row.field);

          return (
            <div key={row.field} className="candidate-info-row">
              <div className="candidate-info-row-icon">{FIELD_ICON[row.field] ?? <IcUser />}</div>
              <div className="candidate-info-row-copy">
                <div className="candidate-info-row-label">{row.label}</div>
                {isHidden ? (
                  <div className="candidate-info-hidden-row">
                    <span className="candidate-info-hidden-copy">
                      <IcLock />
                      Hidden for privacy
                    </span>
                    <button
                      type="button"
                      className="candidate-request-access-button"
                      onClick={() => handleRequestAccess(row.field)}
                      disabled={requestedFields.includes(row.field)}
                    >
                      {requestedFields.includes(row.field) ? 'Requested' : 'Request access'}
                    </button>
                  </div>
                ) : withheld ? (
                  <div className="candidate-info-row-value candidate-info-row-value--withheld">
                    <IcLock />
                    {WITHHELD_COPY}
                  </div>
                ) : (
                  <div className="candidate-info-row-value">{row.value}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {userData?.linkedin && String(userData.linkedin).includes('linkedin.com') ? (
        <a
          href={String(userData.linkedin)}
          target="_blank"
          rel="noopener noreferrer"
          className="candidate-info-row-linkedin"
        >
          View LinkedIn profile ↗
        </a>
      ) : null}
    </div>
  );
};

export default PublicUserInformationList;
